import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DiefCfemContext } from '../../../contexts/DiefCfemContext';
import { ICrumb } from '../../../interfaces/ICrumb';
import TituloSection from '../../../components/tituloSection/tituloSection';
import CustomDivider from '../../../components/customDivider/customDivider';
import styles from './HastaPublicaView.module.scss';
import { formatCurrency } from '../../../utils/formatCurrency';
import { formatDateTime } from '../../../utils/formatDate';
import { BrButton, BrLoading, BrMessage, BrSelect } from '@govbr-ds/react-components';
import { useReactToPrint } from 'react-to-print';
import { useQuery } from '@tanstack/react-query';
import { IArrematacaoListagemResumo } from '../../../interfaces/IArrematacao';
import { arrematacoesTotais } from '../../../utils/arrematacoesTotais';
import { IDeclaracaoHastaPublica } from '../../../interfaces/IDeclaracoes';
import { mesParaAbreviacao } from '../../../utils/mesParaAbreviacao';
import generatePDF from 'react-to-pdf';
import { useBackEndRequests } from '../lib/backEndRequests';
import { ArrematacaoTableResumo } from '../components/hastaPublicaResumo/components/arrematacaoTableResumo/arrematacaoTableResumo';
import ErrorMessage from '../../../components/errorMessage/errorMessage';
import HastaPublicaIdBasica from '../components/hastaPublicaIdBasica/hastaPublicaIdBasica';
import { useNotificacao } from '../../../hooks/useNotificacao';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';

const LINK_EMISSAO_BOLETO = 'https://app.anm.gov.br/BoletosCfem/NaoDivida/CfemHastaPublica';

interface ISelectOption {
  label: string;
  value: string;
  tipo: string;
}

function HastaPublicaView() {
  const urlParams = new URLSearchParams(window.location.search);
  let declaracaoId = urlParams.get('id') || '';
  const declaracaoRaiz = declaracaoId;
  const status = urlParams.get('status') || '';
  const [statusError, setStatusError] = useState<string | null>(null);

  const mostrarNotificacao = useNotificacao();

  const [arrematacaoListagemData, setArrematacaoListagemData] = useState<IArrematacaoListagemResumo[]>([]);
  const { getDeclaracaoById, getRetificarDeclaracao } = useBackEndRequests();
  const [isResumeOpen, setIsResumeOpen] = useState(true);

  const [listaDeclaracoes, setListaDeclaracoes] = useState<ISelectOption[]>([]);
  const [declaracaoSelecionada, setDeclaracaoSelecionada] = useState<ISelectOption>();

  const {
    data: declaracaoData,
    isLoading,
    isFetching,
    error,
    refetch: refetchDeclaracao,
  } = useQuery<IDeclaracaoHastaPublica, IErrorWithSentryId>({
    queryKey: ['declaracaoById', declaracaoId],
    queryFn: () => getDeclaracaoById(declaracaoId),
    refetchOnWindowFocus: false,
    retry: statusError === '404' || statusError === '400' || statusError === '403',
  });

  useEffect(() => {
    if (declaracaoData) {
      if (declaracaoData.tipoDeclarante !== 2) setStatusError('400');
      const novaListagemData: IArrematacaoListagemResumo[] = arrematacoesTotais(declaracaoData.arrematacoes || []);

      if (listaDeclaracoes.length === 0) {
        formatarListaDeclaracoes(declaracaoData.retificadoras);
      }

      setArrematacaoListagemData(novaListagemData);
      setIsResumeOpen(false);
    } else if ((declaracaoData !== undefined && !declaracaoData) || !!error) {
      setStatusError(error?.status?.toString() ?? null);
    }
  }, [declaracaoData, error]);

  const navigate = useNavigate();

  const context = useContext(DiefCfemContext);

  useEffect(() => {
    const crumbs: ICrumb[] = [
      {
        isHome: true,
        label: 'home',
        onClick: () => {
          navigate('/');
        },
      },
      {
        label: 'Hasta Pública',
        onClick: () => {
          navigate('/hasta-publica');
        },
      },
      {
        active: true,
        label: 'Declaração Enviada',
      },
    ];
    context?.setCrumbs(crumbs);
  }, [navigate]);

  const iconClass = !isResumeOpen ? 'fas fa-angle-up' : 'fas fa-angle-down';

  const accordionHeaderStyle = isResumeOpen ? styles.open : '';

  const contentRef = useRef<HTMLDivElement>(null);
  const reactToPrintFn = useReactToPrint({
    contentRef,
    pageStyle: `@page {
      size: 210mm 148mm;
      }
      @media print {
      @page {  size: landscape;
          margin: 5mm !important;
      }
      @media all {
                      .pagebreak {
                        overflow: visible; 
                      }
                  }
              }
          }`,
    onAfterPrint: () => {
      setIsResumeOpen(false);
    },
  });

  const formatarListaDeclaracoes = (listaDeclaracao: any[]) => {
    const numDeclaracoes = listaDeclaracao.length;

    const listaFormatada = listaDeclaracao.map((dec, index) => {
      const posicao = `(${numDeclaracoes - index}/${numDeclaracoes})`;
      const tipo = dec.tipoDeclaracao === 1 ? 'Retificadora' : 'Original';

      return {
        value: dec.id,
        label: `${tipo} - ${dec.dataEmissaoNF} ${posicao}`,
        tipo: `${tipo} ${posicao}`,
      };
    });

    setListaDeclaracoes(listaFormatada);

    const declaracaoExibida = listaFormatada.find((dec) => dec.value === declaracaoId);
    setDeclaracaoSelecionada(declaracaoExibida);
  };

  const printDeclaracao = () => {
    setIsResumeOpen(true);
    contentRef?.current?.focus();
    setTimeout(() => {
      reactToPrintFn();
    }, 1000);
  };

  const generatePDFDeclaracao = () => {
    setIsResumeOpen(true);
    contentRef?.current?.focus();
    setTimeout(() => {
      generatePDF(contentRef, {
        filename: `recibo_declaracao_${mesParaAbreviacao(
          declaracaoData?.exercicioMes ?? -1
        )}_${declaracaoData?.exercicioAno}_${declaracaoData?.numeroRecibo}.pdf`,
        page: {
          margin: 5,
        },
      });
    }, 1000);
  };

  const retificarDeclaracao = async () => {
    if (declaracaoRaiz) {
      await getRetificarDeclaracao(declaracaoRaiz)
        .then(async (reficadoraId) => {
          navigate(`/hasta-publica/edit?id=${reficadoraId}`);
        })
        .catch((err) => {
          mostrarNotificacao({
            tipo: 'danger',
            conteudo: 'Erro ao retificar a declaração.',
            ...(err && { complementoSentryId: err }),
          });
        });
    } else {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Não foi possível retificar a declaração neste momento',
      });
    }
  };

  const errorTitle = () => {
    if (statusError === '404' || statusError === '400') {
      return 'Declaração não encontrada.';
    } else if (statusError === '403') {
      return 'Acesso negado.';
    } else return undefined;
  };

  const errorDescription = () => {
    if (statusError === '404' || statusError === '400') {
      return 'A declaração que você está tentando acessar não foi encontrada. Por favor, verifique se o link está correto e tente novamente.';
    } else if (statusError === '403') {
      return 'Você não tem permissão para acessar essa declaração. Por favor, entre em contato com o suporte pelo e-mail corfem@anm.gov.br caso ache que isto é um erro.';
    } else return undefined;
  };

  const onChangeDeclaracao = async (value: string) => {
    const declaracaoSelecionada = listaDeclaracoes.find((m) => m.value.toString() === value);
    if (declaracaoSelecionada) {
      setDeclaracaoSelecionada(declaracaoSelecionada);
      declaracaoId = declaracaoSelecionada.value;

      refetchDeclaracao();
    }
  };

  return (
    <>
      <CustomDivider step={3} mes={declaracaoData?.exercicioMes} ano={declaracaoData?.exercicioAno} />
      <TituloSection type='hasta-publica' isSteps />
      {status === 'enviado' && (
        <BrMessage inlineTitle message='Sua declaração foi enviada!' title='Sucesso.' status='success' closable />
      )}
      {(isLoading || isFetching) && (
        <div className={styles.loadContainer}>
          <BrLoading large label='Carregando...' />
        </div>
      )}
      {statusError !== null ? (
        <ErrorMessage
          errorMessage={error?.message ?? ''}
          errorTitle={errorTitle()}
          errorDescription={errorDescription()}
          error={error}
        />
      ) : (
        <>
          <div ref={contentRef}>
            <BrMessage
              inlineTitle
              message='De acordo com o art. 8º da Resolução ANM nº 156, de 8 de abril de 2024, a DIEF-CFEM apresentada na forma estabelecida por esta Resolução constitui confissão de dívida e instrumento hábil e suficiente para a exigência dos créditos da CFEM nela consignados (base legal: art. 2º-A, § 5º, da Lei nº 8.001/1990). Emita a(s) guia(s) de recolhimento da CFEM e efetue o(s) pagamento(s) de acordo com os valores declarados.'
              status='info'
              closable
            />
            <div className={styles.container}>
              <div className={styles.headerContainer}>
                <div className={styles.titleContainer}>
                  <span>
                    DIEF-CFEM - Entregue - {mesParaAbreviacao(declaracaoData?.exercicioMes || -1)}/
                    {declaracaoData?.exercicioAno} <i className={`fas fa-check-circle fa-lg ${styles.greenIcon}`} />
                  </span>
                  <span className={styles.subtitle}>Arrematação em Hasta Pública</span>
                </div>

                <div className={styles.selectContainer}>
                  <BrSelect
                    label='Declaração'
                    value={declaracaoSelecionada?.value}
                    onChange={(value: string) => onChangeDeclaracao(value)}
                    options={listaDeclaracoes}
                  />
                </div>
              </div>
              <div className={styles.statusContainer}>
                <div className={styles.ReciboCard}>
                  <i className={`fas fa-receipt ${styles.greenIcon}`} />
                  <div className={styles.textRecibo}>
                    <span className={styles.statusTitle}>Recibo da declaração</span>
                    <span className={styles.statusContent}>{declaracaoData?.numeroRecibo}</span>
                  </div>
                </div>
                <div className={styles.statusCard}>
                  <span className={styles.statusTitle}>Id da declaração</span>
                  <span className={styles.statusContent}>{declaracaoData?.id || ''}</span>
                </div>
                <div className={styles.statusCard}>
                  <span className={styles.statusTitle}>Tipo</span>
                  <span className={styles.statusContent}>{declaracaoSelecionada?.tipo}</span>
                </div>
                <div className={styles.statusCard}>
                  <span className={styles.statusTitle}>Data de entrega</span>
                  <span className={styles.statusContent}>{formatDateTime(declaracaoData?.dataRecibo || '')}</span>
                </div>
              </div>
              <div className={`br-accordion ${styles.accordion}`}>
                <div className={`item ${styles.accordionItem}`}>
                  <button
                    className={`header ${accordionHeaderStyle}`}
                    type='button'
                    onClick={() => setIsResumeOpen(!isResumeOpen)}
                  >
                    <span className={`icon`}>
                      <i className={iconClass}></i>
                    </span>
                    <span className={`title`}>Resumo da Declaração</span>
                  </button>
                  {isResumeOpen && (
                    <div className={styles.accordionContent}>
                      <HastaPublicaIdBasica
                        declaranteData={declaracaoData?.declarante}
                        isDeclaranteDataLoading={isLoading}
                        noBorder
                        regimeTributacaoSalvo={declaracaoData?.regimeTributacao?.nome || 'Não cadastrado'}
                      />
                      <div className={styles.statusContainer}>
                        <div className={styles.statusCard}>
                          <span className={styles.statusTitle}>Valor total de arrematações</span>
                          <span className={styles.statusContent}>
                            {formatCurrency(declaracaoData?.valorTotalArrematacoes ?? '')}
                          </span>
                        </div>
                      </div>
                      <ArrematacaoTableResumo data={arrematacaoListagemData} isLoading={isLoading} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className={styles.actionsContainer}>
            <div className={styles.servicosContainer}>
              <span className={styles.servicosTitle}>Serviços Disponíveis</span>
              <div className={styles.servicos}>
                <BrButton onClick={() => generatePDFDeclaracao()}>
                  <i className={`fas fa-file-download`} /> Baixar Declaração
                </BrButton>

                <BrButton onClick={() => printDeclaracao()}>
                  <i className={`fas fa-print`} /> Imprimir Declaração
                </BrButton>

                <BrButton onClick={() => window.open(LINK_EMISSAO_BOLETO, '_blank', 'noopener')}>
                  <i className={`fas fa-barcode`} /> Emitir Guia de Recolhimento
                </BrButton>

                <BrButton onClick={() => retificarDeclaracao()}>
                  <i className={`fas`}>&#xf2f1;</i> Retificar Declaração
                </BrButton>
              </div>
            </div>
            <div className={styles.actions}>
              <BrButton secondary onClick={() => navigate('/hasta-publica')}>
                <i className={`fas fa-times`} /> Fechar
              </BrButton>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default HastaPublicaView;
